import { useMemo } from 'react';
import { Receipt, type LucideIcon } from 'lucide-react';
import { useLingui } from '@lingui/react';
import type { MessageDescriptor } from '@lingui/core';
import { differenceInCalendarDays, formatDistanceToNow } from 'date-fns';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { getDateFnsLocale } from '@/lib/format';
import { formatDate, parseDateString } from '@/utils/dateUtils';
import {
  BILLED_LABEL,
  deriveSettlingInStage,
  getSettlingInStatus,
  type SettlingInStage,
} from '@/lib/settlingInStatus';
import type { SettlingInPlanDetail } from '@/types/settlingIn';

type NodeState = 'reached' | 'current' | 'upcoming';

interface StepperNode {
  key: string;
  label: MessageDescriptor;
  icon: LucideIcon;
  state: NodeState;
  date?: string;
}

export type LifecycleStepperPlan = Pick<
  SettlingInPlanDetail,
  'start_date' | 'end_date' | 'settling_in_completed' | 'sign_off_date' | 'is_billed'
>;

interface LifecycleStepperProps {
  plan: LifecycleStepperPlan;
  className?: string;
}

/**
 * Display-only lifecycle stepper for the detail header (spec §2.3).
 *
 * Shape is load-bearing and NOT strictly linear:
 *
 *     ① Planned ─ ② Running ─┬─ ③ Completed ─┬─ ⑤ Billed
 *                            └─ ④ Signed off ┘
 *
 * ③ and ④ are independent, parallel prerequisites of ⑤, evaluated separately —
 * never as `completed && signedOff` in sequence. A signed-off-but-not-completed
 * plan (and the converse) is a real, reachable state and must render correctly.
 *
 * ⑤ Billed reads the server's `is_billed` verbatim (ST-3 landed it on the detail
 * response). It is never inferred from ③ ∧ ④ — that would light up a state the
 * server never confirmed.
 *
 * Non-interactive and keyboard-skippable by construction: no button, no onClick,
 * no tabIndex, no focusable descendant — Tab jumps from the page heading straight
 * past the whole stepper to the ⋯ Actions menu.
 */
export function LifecycleStepper({ plan, className }: LifecycleStepperProps) {
  const { i18n } = useLingui();
  const locale = getDateFnsLocale(i18n.locale);

  const nodes = useMemo(() => {
    const stage = deriveSettlingInStage(plan);
    const start = parseDateString(plan.start_date);
    // Calendar-day comparison, matching `deriveSettlingInStage` exactly — a
    // timestamp compare would disagree with the stage it renders alongside, and
    // the repo counts days with `differenceInCalendarDays`, never by subtracting
    // timestamps (DST).
    const hasStarted = !!start && differenceInCalendarDays(start, new Date()) <= 0;

    const planned: StepperNode = {
      key: 'planned',
      ...pick('planned'),
      // A not-yet-started plan sits AT ① — hardcoding `reached` left the whole
      // stepper with no `current` node, the one thing it exists to show.
      state: stage === 'planned' ? 'current' : 'reached',
      date: plan.start_date,
    };
    const running: StepperNode = {
      key: 'running',
      ...pick('running'),
      state: stage === 'running' ? 'current' : hasStarted ? 'reached' : 'upcoming',
      date: hasStarted ? plan.start_date : undefined,
    };
    // ③ and ④ are read independently off their own field — this is the
    // parallel-prerequisite shape, not a sequence. Only the `current` overlay
    // comes from the derived stage; the reached/upcoming fallback still reads
    // each node's own field, so the two stay independent.
    //
    // Without the stage check these two could only ever be `reached` or
    // `upcoming`, leaving a completed or signed-off plan with NO current node —
    // the one thing the stepper exists to show. `deriveSettlingInStage` returns
    // exactly one stage, so exactly one node is `current` in every state.
    const completed: StepperNode = {
      key: 'completed',
      ...pick('completed'),
      state:
        stage === 'completed'
          ? 'current'
          : plan.settling_in_completed === true
            ? 'reached'
            : 'upcoming',
      date: plan.settling_in_completed === true ? plan.end_date : undefined,
    };
    const signedOff: StepperNode = {
      key: 'signed_off',
      ...pick('signed_off'),
      state: stage === 'signed_off' ? 'current' : plan.sign_off_date ? 'reached' : 'upcoming',
      date: plan.sign_off_date,
    };
    const billed: StepperNode = {
      key: 'billed',
      label: BILLED_LABEL,
      icon: Receipt,
      // Read straight off the server's `is_billed`. `deriveSettlingInStage`
      // deliberately never returns `billed` client-side, so this node is only
      // ever `reached` or `upcoming` — never `current`. It carries no date: the
      // DTO has no billed-at field, and inventing one would be a lie.
      state: plan.is_billed === true ? 'reached' : 'upcoming',
      date: undefined,
    };

    return { planned, running, completed, signedOff, billed };
  }, [plan]);

  const groupLabel = i18n._({
    id: 'settlingIn.stepper.ariaLabel',
    message: 'Settling-in lifecycle',
  });

  const renderNode = (node: StepperNode) => {
    const Icon = node.icon;
    const parsed = parseDateString(node.date);
    const isUpcoming = node.state === 'upcoming';

    return (
      // Current status is otherwise conveyed only through font weight and
      // colour — both invisible to a screen reader. `aria-current="step"` is the
      // semantic channel for it, and exactly one node ever carries it.
      <div
        className="flex items-center gap-2"
        aria-current={node.state === 'current' ? 'step' : undefined}
      >
        <Icon
          className={cn(
            'h-4 w-4 shrink-0',
            node.state === 'reached' && 'text-capacity-normal-on-surface',
            node.state === 'current' && 'text-foreground',
            isUpcoming && 'text-muted-foreground/50',
          )}
          aria-hidden="true"
        />
        <div className="flex flex-col">
          <span
            className={cn(
              'text-sm leading-tight',
              node.state === 'current' && 'font-semibold text-foreground',
              node.state === 'reached' && 'text-foreground',
              isUpcoming && 'text-muted-foreground',
            )}
          >
            {i18n._(node.label)}
          </span>
          <span className="text-xs text-muted-foreground leading-tight">
            {node.date && parsed ? (
              <>
                {formatDate(node.date, i18n.locale)}
                {node.state !== 'upcoming' && (
                  <span className="ml-1">
                    ({formatDistanceToNow(parsed, { addSuffix: true, locale })})
                  </span>
                )}
              </>
            ) : (
              i18n._({ id: 'settlingIn.stepper.notReached', message: '—' })
            )}
          </span>
        </div>
      </div>
    );
  };

  const connector = <Separator className="w-6 shrink-0" aria-hidden="true" />;

  return (
    // German runs 100–200 % long on short strings: wrap-tolerant flex, no fixed widths.
    <div
      role="group"
      aria-label={groupLabel}
      data-testid="lifecycle-stepper"
      className={cn('flex flex-wrap items-center gap-x-2 gap-y-3', className)}
    >
      {renderNode(nodes.planned)}
      {connector}
      {renderNode(nodes.running)}
      {connector}

      {/* ③ + ④: the parallel branch — two independent prerequisites of ⑤. */}
      <div className="flex flex-col gap-2">
        {renderNode(nodes.completed)}
        {renderNode(nodes.signedOff)}
      </div>

      {connector}
      {renderNode(nodes.billed)}
    </div>
  );
}

/** Label + icon for a stage, sourced from the shared status lib so the stepper
 *  and the list lozenge cannot drift. */
function pick(stage: SettlingInStage): { label: MessageDescriptor; icon: LucideIcon } {
  const style = getSettlingInStatus(stage);
  return { label: style.label, icon: style.icon };
}
